import { useMemo } from "react";
import ReactECharts from "echarts-for-react";

import type { Recommendation } from "../api/types";
import { echarts, PALETTE, baseAxis, baseTooltip } from "../charts/echarts";

type Horizon = "intraday" | "short" | "medium" | "long";

export default function ScoreHistory({
  history,
  loading,
  horizon,
  onHorizon,
}: {
  history: Recommendation[];
  loading: boolean;
  horizon: Horizon;
  onHorizon: (h: Horizon) => void;
}) {
  const option = useMemo(() => {
    const data = history.map((r) => [r.generated_at, r.score]);
    return {
      backgroundColor: "transparent",
      animation: false,
      tooltip: {
        ...baseTooltip,
        valueFormatter: (v: unknown) => (typeof v === "number" ? v.toFixed(0) : String(v)),
      },
      grid: { left: 36, right: 16, top: 10, bottom: 24 },
      xAxis: { ...baseAxis, type: "time" },
      yAxis: {
        ...baseAxis,
        type: "value",
        min: 0,
        max: 100,
        splitNumber: 4,
      },
      series: [
        {
          name: "Score",
          type: "line",
          data,
          smooth: false,
          showSymbol: history.length < 30,
          symbolSize: 4,
          lineStyle: { color: PALETTE.accent, width: 2 },
          itemStyle: { color: PALETTE.accent },
          markLine: {
            silent: true,
            symbol: "none",
            label: { color: PALETTE.muted, fontSize: 9 },
            data: [
              { yAxis: 65, lineStyle: { color: PALETTE.bull, type: "dashed", width: 1 }, label: { formatter: "bull", position: "insideEndTop", color: PALETTE.bull } },
              { yAxis: 35, lineStyle: { color: PALETTE.bear, type: "dashed", width: 1 }, label: { formatter: "bear", position: "insideEndBottom", color: PALETTE.bear } },
            ],
          },
        },
      ],
    } satisfies echarts.EChartsCoreOption;
  }, [history]);

  return (
    <section className="rounded-2xl border border-slate-800 bg-slate-900 p-4">
      <header className="mb-3 flex items-center justify-between">
        <div className="text-sm font-medium text-slate-300">Score history</div>
        <div className="flex gap-1 rounded-md border border-slate-700 bg-slate-950 p-0.5 text-[11px]">
          {(["intraday", "short", "medium", "long"] as Horizon[]).map((h) => (
            <button
              key={h}
              onClick={() => onHorizon(h)}
              className={`rounded px-2 py-0.5 ${
                horizon === h ? "bg-slate-700 text-slate-100" : "text-slate-400 hover:text-slate-200"
              }`}
            >
              {h}
            </button>
          ))}
        </div>
      </header>
      {loading ? (
        <div className="text-sm text-slate-500">Loading…</div>
      ) : history.length < 2 ? (
        <div className="text-sm text-slate-500">
          Not enough data points yet. Rescore runs every 30 min.
        </div>
      ) : (
        <ReactECharts option={option} style={{ height: 220, width: "100%" }} notMerge={true} lazyUpdate={true} echarts={echarts} />
      )}
    </section>
  );
}
